import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../../../../convex/_generated/api';
import { ArrowLeft, User, Award, TrendingUp, ClipboardList } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { Id } from '../../../../../convex/_generated/dataModel';

export default function PlayerDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const { playerId } = useParams<{ playerId: string }>();

  if (!playerId) {
    return <div>Invalid player ID</div>;
  }

  const data = useQuery(
    api.coach.players.getDetail,
    playerId ? { playerId: playerId as Id<"players"> } : 'skip'
  );
  const isLoading = data === undefined;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-card rounded-lg shadow p-8 text-center">
        <p className="text-red-500">Failed to load player details</p>
        <Button onClick={() => navigate(-1)} className="mt-4">
          Go Back
        </Button>
      </div>
    );
  }

  const { player, evaluations } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
      </div>

      {/* Player Info Card */}
      <div className="bg-card rounded-lg shadow p-6">
        <div className="flex items-start gap-6">
          <div className="w-20 h-20 bg-gradient-to-br from-clay-400 to-clay-600 rounded-full flex items-center justify-center">
            <span className="text-2xl font-bold text-white">
              {player.firstName[0]}{player.lastName[0]}
            </span>
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-foreground">
              {player.firstName} {player.lastName}
            </h1>
            <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
              {player.age !== null && (
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  Age {player.age}
                </span>
              )}
              {player.skillLevel && (
                <span className="flex items-center gap-1">
                  <Award className="w-4 h-4" />
                  <span className="capitalize">{player.skillLevel}</span>
                </span>
              )}
              {player.utrRating !== null && (
                <span className="flex items-center gap-1">
                  <TrendingUp className="w-4 h-4" />
                  UTR {player.utrRating.toFixed(1)}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-muted/40 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{player.attendancePercent}%</p>
            <p className="text-sm text-muted-foreground">Attendance</p>
          </div>
          <div className="bg-muted/40 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{player.totalEvaluations}</p>
            <p className="text-sm text-muted-foreground">Evaluations</p>
          </div>
          {evaluations.length > 0 && (
            <>
              <div className="bg-muted/40 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-purple-600">{evaluations[0].average}</p>
                <p className="text-sm text-muted-foreground">Latest Score</p>
              </div>
              <div className="bg-muted/40 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-amber-600">
                  {(evaluations.reduce((sum, e) => sum + e.average, 0) / evaluations.length).toFixed(1)}
                </p>
                <p className="text-sm text-muted-foreground">Avg Score</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Recent Evaluations */}
      <div className="bg-card rounded-lg shadow">
        <div className="p-4 border-b border-border flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-muted-foreground" />
          <h2 className="font-semibold text-foreground">Recent Evaluations</h2>
          <span className="text-sm text-muted-foreground">({evaluations.length})</span>
        </div>

        {evaluations.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            No evaluations recorded yet
          </div>
        ) : (
          <div className="divide-y">
            {evaluations.map((evaluation) => (
              <div key={evaluation.id} className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">
                    {new Date(evaluation.createdAt).toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                  <span className={cn(
                    'text-lg font-bold',
                    evaluation.average >= 3.5 ? 'text-green-600' :
                    evaluation.average >= 2.5 ? 'text-amber-600' :
                    'text-red-600'
                  )}>
                    {evaluation.average}/4
                  </span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <MetricPill label="Effort" value={evaluation.effort} />
                  <MetricPill label="Engage" value={evaluation.engagement} />
                  <MetricPill label="Compete" value={evaluation.competitionLevel} />
                  <MetricPill label="Attitude" value={evaluation.attitude} />
                </div>
                {evaluation.notes && (
                  <p className="mt-2 text-sm text-muted-foreground italic">
                    &ldquo;{evaluation.notes}&rdquo;
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MetricPill({ label, value }: { label: string; value: number }) {
  const colorClass = value >= 4 ? 'bg-green-100 text-green-700' :
                     value >= 3 ? 'bg-blue-100 text-blue-700' :
                     value >= 2 ? 'bg-amber-100 text-amber-700' :
                     'bg-red-100 text-red-700';

  return (
    <div className={cn('rounded px-2 py-1 text-center text-xs', colorClass)}>
      <span className="font-medium">{label}</span>
      <span className="ml-1">{value}</span>
    </div>
  );
}
